import { Fragment, ReactNode, useMemo, useState } from 'react';
import { buildCsv, downloadCsv, formatCell } from './marketingCsv';

export interface DataTableColumn<Row> {
  key: string;
  header: string;
  accessor: (row: Row) => string | number | null | undefined;
  format?: 'int' | 'aed' | 'pct' | 'text';
  align?: 'start' | 'end';
  sortable?: boolean;
  suppressedWhenNull?: boolean;
  tooltipWhenNull?: string;
}

export interface DataTableProps<Row> {
  caption: string;
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  emptyText?: string;
  exportMeta?: { from: string; to: string; timezone: string };
  renderExpanded?: (row: Row) => ReactNode;
  testId?: string;
}

type Sort = { key: string; dir: 'asc' | 'desc' };

const isNumeric = <Row,>(c: DataTableColumn<Row>) => (c.format ?? 'text') !== 'text';
const alignOf = <Row,>(c: DataTableColumn<Row>) => c.align ?? (isNumeric(c) ? 'end' : 'start');

/** Stable sort by one column; nulls always last, numbers numerically, text with localeCompare. */
function useSortedRows<Row>(columns: DataTableColumn<Row>[], rows: Row[], sort: Sort | null): Row[] {
  return useMemo(() => {
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!sort || !col) return rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return rows
      .map((row, i) => ({ row, i, v: col.accessor(row) }))
      .sort((a, b) => {
        const an = a.v == null, bn = b.v == null;
        if (an || bn) return an && bn ? a.i - b.i : an ? 1 : -1;
        const d = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : String(a.v).localeCompare(String(b.v), 'en');
        return d !== 0 ? sign * d : a.i - b.i;
      })
      .map((x) => x.row);
  }, [columns, rows, sort]);
}

/** Spec 084 — sortable, keyboard-operable table with CSV export, suppressed/null cells and optional expandable rows. */
export default function DataTable<Row>({
  caption, columns, rows, rowKey, defaultSort, emptyText = 'No data in this range', exportMeta, renderExpanded, testId,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState<Sort | null>(defaultSort ?? null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const sorted = useSortedRows(columns, rows, sort);
  const toggleSort = (key: string) => setSort((s) => {
    if (s?.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' };
    const col = columns.find((c) => c.key === key);
    return { key, dir: col && isNumeric(col) ? 'desc' : 'asc' };
  });
  const toggleOpen = (id: string) => setOpen((s) => { const n = new Set(s); if (!n.delete(id)) n.add(id); return n; });
  const exportCsv = () =>
    downloadCsv(`${caption}-${exportMeta?.from ?? 'all'}-${exportMeta?.to ?? 'all'}.csv`, buildCsv(columns, sorted, exportMeta));
  const span = columns.length + (renderExpanded ? 1 : 0);

  return (
    <div className="mkt-dt" data-testid={testId}>
      <div className="mkt-dt__bar">
        <button type="button" className="mkt-dt__export" onClick={exportCsv} disabled={rows.length === 0}>Export CSV</button>
      </div>
      <div className="overflow-x-auto mkt-dt__scroll">
        <table role="table" className="admin-table mkt-dt__table">
          <caption className="mkt-dt__caption">{caption}</caption>
          <thead>
            <tr>
              {renderExpanded && <th scope="col"><span className="sr-only">Details</span></th>}
              {columns.map((c) => {
                const active = sort?.key === c.key ? sort.dir : null;
                const cls = alignOf(c) === 'end' ? 'mkt-num' : undefined;
                return (
                  <th key={c.key} scope="col" className={cls}
                    aria-sort={c.sortable === false ? undefined : active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}>
                    {c.sortable === false ? c.header : (
                      <button type="button" className="mkt-sort" onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}>
                        {c.header} <span aria-hidden="true">{active === 'asc' ? '↑' : active === 'desc' ? '↓' : '↕'}</span>
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={span} className="mkt-empty" data-testid="datatable-empty">{emptyText}</td></tr>
            )}
            {sorted.map((row) => {
              const id = rowKey(row);
              const expanded = open.has(id);
              const panelId = `mkt-dt-${id.replace(/\W/g, '_')}`;
              return (
                <Fragment key={id}>
                  <tr>
                    {renderExpanded && (
                      <td>
                        <button type="button" className="mkt-dt__toggle" aria-expanded={expanded} aria-controls={panelId}
                          aria-label={`${expanded ? 'Hide' : 'Show'} details`} onClick={() => toggleOpen(id)}>
                          <span aria-hidden="true">{expanded ? '−' : '+'}</span>
                        </button>
                      </td>
                    )}
                    {columns.map((c) => {
                      const missing = c.accessor(row) == null;
                      return (
                        <td key={c.key} className={alignOf(c) === 'end' ? 'mkt-num' : undefined}
                          title={missing && !c.suppressedWhenNull ? c.tooltipWhenNull : undefined}>
                          {formatCell(c, row)}
                        </td>
                      );
                    })}
                  </tr>
                  {renderExpanded && expanded && (
                    <tr id={panelId} className="mkt-dt__expanded"><td colSpan={span}>{renderExpanded(row)}</td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
